(() => {
const trustRoot = document.querySelector("[data-trust-homepage]");
if (!trustRoot) return;

const announcementBar = document.querySelector(".announcement-bar-section");
const syncHomepageHeaderOffset = () => {
  document.documentElement.style.setProperty(
    "--trust-announcement-height",
    `${announcementBar?.offsetHeight || 0}px`,
  );
};

syncHomepageHeaderOffset();
if (announcementBar && "ResizeObserver" in window) {
  new ResizeObserver(syncHomepageHeaderOffset).observe(announcementBar);
}

let trustAssets = {};
try {
  trustAssets = JSON.parse(
    trustRoot.querySelector("[data-trust-assets]")?.textContent || "{}",
  );
} catch {
  trustAssets = {};
}

const heroRetailersImage = window.matchMedia("(max-width: 760px)").matches
  ? trustAssets["homepage-hero-retailers-mobile.avif"]
  : trustAssets["homepage-hero-retailers.avif"];

const scrollRestoreKey = `lumenora-scroll:${window.location.pathname}`;
const navigationType =
  window.performance.getEntriesByType?.("navigation")[0]?.type ?? "navigate";
const shouldRestoreScroll =
  navigationType === "reload" || navigationType === "back_forward";
let savedScrollY = 0;

try {
  savedScrollY = shouldRestoreScroll
    ? Number(window.sessionStorage.getItem(scrollRestoreKey)) || 0
    : 0;
} catch {
  savedScrollY = 0;
}

window.addEventListener("pagehide", () => {
  try {
    window.sessionStorage.setItem(scrollRestoreKey, String(window.scrollY));
  } catch {
    // Scroll restoration is a progressive enhancement.
  }
});

const proofData = {
  moq: {
    label: "THE SMALL-ORDER ADVANTAGE",
    title: "Order one piece or a thousand.",
    description:
      "You do not tie up capital in inventory, and you do not have to guess what will sell.",
    evidence: "Evidence: mixed-SKU receiving and consolidation",
    image: heroRetailersImage,
    position: "center",
  },
  dispatch: {
    label: "WAREHOUSE-LED FULFILLMENT",
    title: "Orders leave our warehouse within three days.",
    description:
      "Faster delivery means better store ratings for sellers, and schedules you can actually plan around for projects.",
    evidence: "Evidence: receiving, QC, packing, and dispatch records",
    image: trustAssets["homepage-dispatch-v2.avif"],
    position: "center",
  },
  breakage: {
    label: "PACKED FOR THE PRODUCT",
    title: "Breakage stays under 1.5% because packing is controlled here.",
    description:
      "Lighting is glass, ceramic, and thin metal. Every point of breakage is margin off your bottom line and a refund request from your customer.",
    evidence: "Evidence: product-specific inserts and inspection checkpoints",
    image: trustAssets["homepage-breakage-packing-v2.avif"],
    position: "center",
  },
};

const hotspotData = {
  function: {
    step: "CHECKPOINT 01 / 04",
    title: "Function test",
    copy:
      "Every fixture is powered on before packing. Drivers, switches, dimming behavior, and visible flicker are checked at our own bench.",
    owner: "Warehouse QC",
    record: "Pass / hold / replace",
    image: trustAssets["case-reference-sourcing.webp"],
    alt: "Warehouse specialist powering on and inspecting a black spotlight",
    imageX: 0,
    imageY: 0,
    scale: 1,
  },
  fittings: {
    step: "CHECKPOINT 02 / 04",
    title: "Finish & fittings",
    copy:
      "Surface finish, shade alignment, mounting parts, screws, drivers, and installation accessories are matched against the order.",
    owner: "Order inspector",
    record: "Photo + checklist",
    image: trustAssets["homepage-fittings-check-v2.avif"],
    alt: "Inspector checking the fixture finish, mounting parts, driver, and fasteners",
    imageX: 0,
    imageY: 0,
    scale: 1,
  },
  packing: {
    step: "CHECKPOINT 03 / 04",
    title: "Product-specific packing",
    copy:
      "Internal clearance and movement are checked before the carton is sealed. Fragile shades and metal bodies are isolated from pressure points.",
    owner: "Packing lead",
    record: "Pack standard by SKU",
    image: trustAssets["homepage-product-packing-v2.avif"],
    alt: "Packing lead measuring clearance around a protected pendant light",
    imageX: 0,
    imageY: 0,
    scale: 1,
  },
  traceability: {
    step: "CHECKPOINT 04 / 04",
    title: "Batch traceability",
    copy:
      "Cartons are grouped by order and destination, then recorded before dispatch so missing parts and mixed consignments are caught here.",
    owner: "Dispatch team",
    record: "Order + carton count",
    image: trustAssets["homepage-traceability-v2.avif"],
    alt: "Dispatch coordinator scanning a grouped batch of lighting cartons",
    imageX: 0,
    imageY: 0,
    scale: 1,
  },
};

const clientData = {
  designers: {
    eyebrow: "For interior designers",
    title: "The sourcing arm you do not have to hire.",
    copy:
      "You found the perfect fixture in a photo, from a supplier who wants 200 units, or from a showroom charging four times what it costs. Send us the image or specification and we will source it in Guzhen, inspect it, and ship it to your project.",
    points: [
      "One fixture or a whole floor",
      "Sourced and inspected in Guzhen",
      "Shipped directly to your project",
    ],
    image: trustAssets["case-reference-designers.webp"],
    alt: "Interior designer reviewing plans and material samples",
    caption: "ONE FIXTURE OR A WHOLE FLOOR",
  },
  hospitality: {
    eyebrow: "For hotels & hospitality",
    title: "FF&E lighting on a project timeline, without the MOQ.",
    copy:
      "We source across multiple factories, consolidate into one shipment, inspect every unit, and ship to your site on schedule.",
    points: [
      "Source across multiple factories",
      "One inspected, consolidated shipment",
      "Reordering for a second property is a single message",
    ],
    image: trustAssets["case-reference-hospitality.webp"],
    alt: "Warm hospitality interior with architectural lighting",
    caption: "MULTIPLE SPACES · ONE CONSOLIDATED ORDER",
  },
  retailers: {
    eyebrow: "For lighting retailers",
    title: "Restock in small batches instead of tying up cash in a container.",
    copy:
      "Test new styles without committing to a full production run. Access the same Guzhen supply base as the big importers, at quantities that fit your shop.",
    points: [
      "Test new styles before committing to volume",
      "Quantities that fit an independent shop",
      "The same Guzhen supply base as big importers",
    ],
    image: trustAssets["case-reference-retailers.webp"],
    alt: "Independent lighting showroom with warm pendant lights",
    caption: "MORE RANGE · LESS INVENTORY RISK",
  },
};

const processData = {
  custom: [
    {
      kicker: "Start with anything",
      title: "Send us what you need.",
      copy:
        "A photo, specification, link, inspiration image, or description is enough to begin.",
      time: "About 5 minutes",
      output: "A confirmed sourcing brief",
      short: "Send us what you need",
      summary: "Photo, spec, link, or description",
      signalLabel: "Brief input",
      signal: "Any reference accepted",
      image: trustAssets["case-reference-designers.webp"],
      alt: "Interior designer sharing a lighting reference",
    },
    {
      kicker: "At the source",
      title: "We source it in Guzhen.",
      copy:
        "Our team checks the market and supplier network, then returns suitable options with photos and pricing.",
      time: "Usually 1–2 business days",
      output: "Comparable options and pricing",
      short: "We source it",
      summary: "Options, photos, and pricing",
      signalLabel: "Market search",
      signal: "Guzhen supplier network",
      image: trustAssets["home-hero-banner-v3.webp"],
      alt: "A broad selection of lighting sourced in Guzhen",
    },
    {
      kicker: "Your decision",
      title: "You approve the right option.",
      copy:
        "Compare the alternatives, request a sample when needed, and confirm the specification before we buy.",
      time: "At your pace",
      output: "One confirmed option",
      short: "You approve",
      summary: "Compare and confirm your option",
      signalLabel: "Decision gate",
      signal: "You approve before purchase",
      image: trustAssets["homepage-process-approval-v2.avif"],
      alt: "Buyer comparing lighting samples and approving the selected option",
    },
    {
      kicker: "Warehouse control",
      title: "We buy and inspect.",
      copy:
        "Goods arrive at our Zhongshan warehouse, where function, finish, fittings, and packaging are checked.",
      time: "Before every dispatch",
      output: "A checked and approved order",
      short: "We buy and inspect",
      summary: "Goods received into our warehouse",
      signalLabel: "Warehouse control",
      signal: "Function, finish, fittings",
      image: trustAssets["case-reference-sourcing.webp"],
      alt: "Lighting fixture inspected in the Zhongshan warehouse",
    },
    {
      kicker: "Ready to move",
      title: "We pack and ship.",
      copy:
        "Your order is consolidated, packed to our standard, and dispatched to you, your customer, or the project site.",
      time: "Dispatch within 3 days",
      output: "Tracking and one accountable contact",
      short: "We pack and ship",
      summary: "To you, your customer, or the project",
      signalLabel: "Dispatch standard",
      signal: "Consolidated and tracked",
      image: trustAssets["homepage-dispatch-v2.avif"],
      alt: "Warehouse team consolidating and packing a lighting order for dispatch",
    },
  ],
  catalog: [
    {
      kicker: "Curated inventory",
      title: "Start with products we already know.",
      copy:
        "Browse trade-ready fixtures with specifications, lead times, and public pricing already organized.",
      time: "Available now",
      output: "Specs, lead time, and public price",
      short: "Browse trade-ready products",
      summary: "Known products, specs, and lead times",
      signalLabel: "Catalog status",
      signal: "Inspected before listing",
      image: trustAssets["process-catalog-browse.avif"],
      alt: "Trade buyer browsing a curated lighting catalog on a tablet",
    },
    {
      kicker: "Your trade price",
      title: "Unlock pricing built for resale.",
      copy:
        "Create an account with your email and company name to see wholesale and tiered pricing without requesting every quote.",
      time: "About 1 minute",
      output: "Your wholesale price tier",
      short: "Create a trade account",
      summary: "See wholesale and tiered prices",
      signalLabel: "Account access",
      signal: "Wholesale prices unlocked",
      image: trustAssets["process-catalog-account.avif"],
      alt: "Independent lighting retailer creating a trade account",
    },
    {
      kicker: "No volume pressure",
      title: "Build the order your business needs.",
      copy:
        "Mix categories, order one item, or restock proven products. Branding and custom inserts remain optional.",
      time: "Instant order review",
      output: "Confirmed basket and lead times",
      short: "Place your order",
      summary: "One piece or a mixed order",
      signalLabel: "Order size",
      signal: "1 piece or a mixed order",
      image: trustAssets["process-catalog-order.avif"],
      alt: "Retailer confirming a mixed lighting order on a tablet",
    },
    {
      kicker: "Same warehouse control",
      title: "We inspect before it leaves.",
      copy:
        "Catalog orders still pass warehouse QC, product-specific packing, and tracked dispatch from Zhongshan.",
      time: "Dispatch within 3 days",
      output: "Tracking and one accountable contact",
      short: "We inspect and dispatch",
      summary: "Warehouse QC and tracking",
      signalLabel: "Fulfillment",
      signal: "QC checked and tracked",
      image: trustAssets["process-catalog-fulfillment.avif"],
      alt: "Warehouse team packing a checked lighting order",
    },
  ],
};

const gsapAvailable = Boolean(window.gsap && window.ScrollTrigger);
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const motionEnabled = gsapAvailable && !reduceMotion;
const staticControlAndProcess = trustRoot.classList.contains("motion-light");

const sceneImagePromises = new Map();
const prepareSceneImage = (src) => {
  if (!src) return Promise.resolve();
  if (sceneImagePromises.has(src)) return sceneImagePromises.get(src);

  const image = new Image();
  image.decoding = "async";
  const ready = new Promise((resolve) => {
    image.addEventListener("load", resolve, { once: true });
    image.addEventListener("error", resolve, { once: true });
    image.src = src;
    if (image.complete) resolve();
  }).then(async () => {
    try {
      await image.decode?.();
    } catch {
      // A completed load is enough when explicit decoding is unavailable.
    }
  });

  sceneImagePromises.set(src, ready);
  return ready;
};

if (gsapAvailable) {
  window.gsap.registerPlugin(window.ScrollTrigger);
  trustRoot.classList.add("has-gsap");
}

const setRovingTabs = (buttons, activeButton) => {
  buttons.forEach((button) => {
    const isActive = button === activeButton;
    button.classList.toggle("is-active", isActive);
    button.setAttribute("aria-selected", String(isActive));
    button.tabIndex = isActive ? 0 : -1;
  });
};

const handleArrowNavigation = (event, buttons) => {
  if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
    return;
  }

  event.preventDefault();
  const currentIndex = buttons.indexOf(event.currentTarget);
  const direction = ["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : -1;
  const nextIndex = (currentIndex + direction + buttons.length) % buttons.length;
  buttons[nextIndex].focus();
  buttons[nextIndex].click();
};

const updateProofContent = (content) => {
  trustRoot.querySelector("[data-proof-label]").textContent = content.label;
  trustRoot.querySelector("[data-proof-title]").textContent = content.title;
  trustRoot.querySelector("[data-proof-description]").textContent = content.description;
  trustRoot.querySelector("[data-proof-evidence]").textContent = content.evidence;
};

const proofButtons = [...trustRoot.querySelectorAll("[data-proof]")];
const heroBaseImage = trustRoot.querySelector(".hero__base-image");
const proofImage = trustRoot.querySelector("[data-proof-image]");
const proofDetailItems = [
  trustRoot.querySelector("[data-proof-label]"),
  trustRoot.querySelector("[data-proof-title]"),
  trustRoot.querySelector("[data-proof-description]"),
  trustRoot.querySelector("[data-proof-evidence]"),
];
let proofSceneRequest = 0;
let currentProofImage = heroBaseImage.currentSrc || heroBaseImage.src;

proofButtons.forEach((button) => {
  const preloadProofImage = () => {
    void prepareSceneImage(proofData[button.dataset.proof]?.image);
  };

  button.addEventListener("pointerenter", preloadProofImage, { once: true });
  button.addEventListener("focus", preloadProofImage, { once: true });

  button.addEventListener("click", async () => {
    if (button.classList.contains("is-active")) return;

    const content = proofData[button.dataset.proof];
    const request = ++proofSceneRequest;
    setRovingTabs(proofButtons, button);
    await prepareSceneImage(content.image);
    if (request !== proofSceneRequest) return;

    if (!motionEnabled) {
      updateProofContent(content);
      heroBaseImage.srcset = "";
      heroBaseImage.src = content.image;
      heroBaseImage.style.objectPosition = content.position;
      proofImage.src = content.image;
      proofImage.style.objectPosition = content.position;
      proofImage.classList.add("is-visible");
      currentProofImage = content.image;
      return;
    }

    window.gsap.killTweensOf([...proofDetailItems, proofImage]);
    heroBaseImage.srcset = "";
    heroBaseImage.src = currentProofImage;
    heroBaseImage.style.objectPosition =
      proofImage.style.objectPosition || "center";
    const proofTimeline = window.gsap.timeline();

    proofTimeline
      .to(proofDetailItems, {
        y: -12,
        opacity: 0,
        duration: 0.18,
        stagger: 0.02,
        ease: "power2.in",
      })
      .add(() => {
        updateProofContent(content);
        proofImage.src = content.image;
        proofImage.style.objectPosition = content.position;
        proofImage.classList.add("is-visible");
        currentProofImage = content.image;
      })
      .fromTo(
        proofImage,
        {
          clipPath: "inset(0 100% 0 0)",
          scale: 1.08,
          opacity: 0.45,
        },
        {
          clipPath: "inset(0 0% 0 0)",
          scale: 1,
          opacity: 1,
          duration: 0.72,
          ease: "power4.out",
        },
        "<",
      )
      .fromTo(
        proofDetailItems,
        { y: 16, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.42,
          stagger: 0.045,
          ease: "power4.out",
        },
        "<+0.12",
      );
  });

  button.addEventListener("keydown", (event) =>
    handleArrowNavigation(event, proofButtons),
  );
});

const warehouseImage = trustRoot.querySelector("[data-warehouse-image]");
const warehouseScan = trustRoot.querySelector(".warehouse__scan");
const warehouseDetailItems = [
  trustRoot.querySelector("[data-hotspot-step]"),
  trustRoot.querySelector("[data-hotspot-title]"),
  trustRoot.querySelector("[data-hotspot-copy]"),
  trustRoot.querySelector(".warehouse__detail dl"),
  trustRoot.querySelector(".warehouse__instruction"),
];
const hotspotButtons = [...trustRoot.querySelectorAll("[data-hotspot]")];

const preloadHotspotImage = (src) => {
  void prepareSceneImage(src);
};

const preloadHotspotImages = () => {
  Object.values(hotspotData).forEach(({ image }) => {
    preloadHotspotImage(image);
  });
};

const warehouseSection = trustRoot.querySelector(".warehouse");
if ("IntersectionObserver" in window && warehouseSection) {
  const warehousePreloadObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      preloadHotspotImages();
      observer.disconnect();
    },
    { rootMargin: "600px 0px" },
  );
  warehousePreloadObserver.observe(warehouseSection);
}

const updateHotspotContent = (content) => {
  trustRoot.querySelector("[data-hotspot-step]").textContent = content.step;
  trustRoot.querySelector("[data-hotspot-title]").textContent = content.title;
  trustRoot.querySelector("[data-hotspot-copy]").textContent = content.copy;
  trustRoot.querySelector("[data-hotspot-owner]").textContent = content.owner;
  trustRoot.querySelector("[data-hotspot-record]").textContent = content.record;
  warehouseImage.src = content.image;
  warehouseImage.alt = content.alt;
};

let hotspotSceneRequest = 0;
let hotspotTimeline;

hotspotButtons.forEach((button) => {
  const preloadButtonImage = () => {
    preloadHotspotImage(hotspotData[button.dataset.hotspot]?.image);
  };

  button.addEventListener("pointerenter", preloadButtonImage, { once: true });
  button.addEventListener("focus", preloadButtonImage, { once: true });

  button.addEventListener("click", async () => {
    const content = hotspotData[button.dataset.hotspot];
    const request = ++hotspotSceneRequest;

    hotspotButtons.forEach((candidate) => {
      const isActive = candidate === button;
      candidate.classList.toggle("is-active", isActive);
      candidate.setAttribute("aria-pressed", String(isActive));
    });

    await prepareSceneImage(content.image);
    if (request !== hotspotSceneRequest) return;

    if (!motionEnabled) {
      updateHotspotContent(content);
      return;
    }

    hotspotTimeline?.kill();
    window.gsap.killTweensOf([
      ...warehouseDetailItems,
      warehouseImage,
      warehouseScan,
      button,
    ]);

    hotspotTimeline = window.gsap.timeline();
    hotspotTimeline
      .to(warehouseDetailItems, {
        y: -12,
        opacity: 0,
        duration: 0.16,
        stagger: 0.015,
        ease: "power2.in",
      }, 0)
      .set(warehouseScan, { y: 0, opacity: 0 }, 0)
      .to(warehouseScan, {
        opacity: 1,
        duration: 0.1,
        ease: "power2.out",
      }, 0.03)
      .to(warehouseScan, {
        y: () => trustRoot.querySelector(".warehouse__image").offsetHeight,
        duration: 0.82,
        ease: "power2.inOut",
      }, 0.03)
      .to(
        warehouseImage,
        {
          opacity: 0.68,
          duration: 0.82,
          ease: "power2.inOut",
        },
        0.03,
      )
      .to(warehouseImage, {
        opacity: 0.08,
        duration: 0.12,
        ease: "power2.in",
      }, 0.82)
      .to(warehouseScan, {
        opacity: 0,
        duration: 0.12,
        ease: "power2.out",
      }, 0.84)
      .add(() => updateHotspotContent(content), 0.94)
      .set(warehouseImage, {
        xPercent: content.imageX,
        yPercent: content.imageY,
        opacity: 0.12,
      }, 0.94)
      .to(warehouseImage, {
        opacity: 1,
        duration: 0.56,
        ease: "power4.out",
      }, 0.94)
      .fromTo(
        warehouseDetailItems,
        { y: 18, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.4,
          stagger: 0.045,
          ease: "power4.out",
        },
        1.04,
      )
      .fromTo(
        button,
        { scale: 0.68 },
        { scale: 1, duration: 0.44, ease: "power4.out" },
        0.94,
      );
  });
});

const clientButtons = [...trustRoot.querySelectorAll("[data-client]")];
const clientImage = trustRoot.querySelector("[data-client-image]");
const clientCopyItems = [
  trustRoot.querySelector("[data-client-eyebrow]"),
  trustRoot.querySelector("[data-client-title]"),
  trustRoot.querySelector("[data-client-copy]"),
  trustRoot.querySelector("[data-client-points]"),
  trustRoot.querySelector(".client-story__copy .text-link"),
];
const clientCaption = trustRoot.querySelector("[data-client-caption]");
const clientCounter = trustRoot.querySelector("[data-client-index]");
let clientSceneRequest = 0;

const updateClientContent = (content, index) => {
  trustRoot.querySelector("[data-client-eyebrow]").textContent = content.eyebrow;
  trustRoot.querySelector("[data-client-title]").textContent = content.title;
  trustRoot.querySelector("[data-client-copy]").textContent = content.copy;
  clientCaption.textContent = content.caption;
  if (clientCounter && Number.isInteger(index)) {
    clientCounter.textContent = String(index + 1).padStart(2, "0");
  }
  trustRoot.querySelector("[data-client-points]").innerHTML = content.points
    .map((point) => `<li>${point}</li>`)
    .join("");
  clientImage.src = content.image;
  clientImage.alt = content.alt;
};

clientButtons.forEach((button) => {
  button.addEventListener("click", async () => {
    const content = clientData[button.dataset.client];
    const clientIndex = clientButtons.indexOf(button);
    const request = ++clientSceneRequest;
    setRovingTabs(clientButtons, button);
    await prepareSceneImage(content.image);
    if (request !== clientSceneRequest) return;

    if (!motionEnabled) {
      updateClientContent(content, clientIndex);
      return;
    }

    window.gsap.killTweensOf([...clientCopyItems, clientImage, clientCaption]);
    const clientTimeline = window.gsap.timeline();

    clientTimeline
      .to(clientCopyItems, {
        y: -14,
        opacity: 0,
        duration: 0.18,
        stagger: 0.018,
        ease: "power2.in",
      })
      .to(
        clientImage,
        {
          clipPath: "inset(0 100% 0 0)",
          scale: 1.04,
          duration: 0.34,
          ease: "power3.in",
        },
        0,
      )
      .to(clientCaption, { xPercent: -100, duration: 0.28, ease: "power3.in" }, 0)
      .add(() => updateClientContent(content, clientIndex))
      .set(clientImage, { clipPath: "inset(0 0 0 100%)", scale: 1.08 })
      .set(clientCaption, { xPercent: 100 })
      .to(clientImage, {
        clipPath: "inset(0 0 0 0%)",
        scale: 1,
        duration: 0.66,
        ease: "power4.out",
      })
      .to(
        clientCaption,
        { xPercent: 0, duration: 0.44, ease: "power4.out" },
        "<+0.08",
      )
      .fromTo(
        clientCopyItems,
        { y: 20, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.42,
          stagger: 0.05,
          ease: "power4.out",
        },
        "<+0.04",
      );
  });

  button.addEventListener("keydown", (event) =>
    handleArrowNavigation(event, clientButtons),
  );
});

const initMobileClientScroll = () => {
  const experience = trustRoot.querySelector(".clients__experience");
  if (!experience || clientButtons.length < 2) return null;

  let activeIndex = Math.max(
    0,
    clientButtons.findIndex((button) => button.classList.contains("is-active")),
  );

  const activateIndex = (index) => {
    const nextIndex = Math.max(0, Math.min(clientButtons.length - 1, index));
    if (nextIndex === activeIndex) return;
    activeIndex = nextIndex;
    clientButtons[nextIndex].click();
  };

  return window.ScrollTrigger.create({
    trigger: experience,
    start: "top top+=68",
    end: "bottom bottom",
    invalidateOnRefresh: true,
    onUpdate: ({ progress }) => {
      const nextIndex = Math.min(
        clientButtons.length - 1,
        Math.floor(progress * clientButtons.length),
      );
      const followingButton = clientButtons[nextIndex + 1];
      if (followingButton) {
        void prepareSceneImage(clientData[followingButton.dataset.client]?.image);
      }
      activateIndex(nextIndex);
    },
  });
};

const faqItems = [...trustRoot.querySelectorAll(".faq-item")];

faqItems.forEach((item) => {
  item.addEventListener("toggle", () => {
    if (!item.open) return;

    faqItems.forEach((otherItem) => {
      if (otherItem !== item) {
        otherItem.open = false;
      }
    });

    if (!motionEnabled) return;

    const answer = item.querySelector(".faq-item__answer");
    window.gsap.killTweensOf(answer);
    window.gsap.fromTo(
      answer,
      { y: 14, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.46,
        ease: "power4.out",
        clearProps: "transform,opacity",
      },
    );
  });
});

const processPathButtons = [...trustRoot.querySelectorAll("[data-process-path]")];
const processStepButtons = [...trustRoot.querySelectorAll("[data-process-step]")];
const processSection = trustRoot.querySelector(".process");
const processImage = trustRoot.querySelector("[data-process-image]");
const processProgress = trustRoot.querySelector("[data-process-progress]");
const processStageItems = [
  trustRoot.querySelector(".process__signal"),
  trustRoot.querySelector("[data-process-kicker]"),
  trustRoot.querySelector("[data-process-title]"),
  trustRoot.querySelector("[data-process-copy]"),
  trustRoot.querySelector(".process__active-copy dl"),
];
let activeProcessPath = "custom";
let activeProcessStep = 0;
let processScrollTriggers = [];
let processStageTimeline;
let processGateLocked = false;
let processGateUnlockTimer;
let processWheelGestureConsumed = false;
let processWheelLastEventAt = 0;
let processWheelPeakDelta = 0;
let processWheelLastDelta = 0;
let processWheelHasDecayed = false;
let processWheelIntentDistance = 0;
let processWheelIntentDirection = 0;
let processSnapGuard = false;
let processSnapGuardTimer;
let processTouchStartY = null;
let processTouchGestureConsumed = false;
let anchorNavigationActive = false;
let anchorNavigationFrame;
let anchorNavigationToken = 0;
let processSceneRequest = 0;
const processGateHoldMs = 1000;
const processWheelResetMs = 200;
const getProcessWheelIntentThreshold = () =>
  Math.max(180, Math.min(260, window.innerHeight * 0.24));

const updateProcessContent = (content, index) => {
  trustRoot.querySelector("[data-process-number]").textContent = String(
    index + 1,
  ).padStart(2, "0");
  trustRoot.querySelector("[data-process-route]").textContent =
    activeProcessPath === "custom"
      ? "Custom sourcing route"
      : "Catalog order route";
  trustRoot.querySelector("[data-process-kicker]").textContent = content.kicker;
  trustRoot.querySelector("[data-process-title]").textContent = content.title;
  trustRoot.querySelector("[data-process-copy]").textContent = content.copy;
  trustRoot.querySelector("[data-process-time]").textContent = content.time;
  trustRoot.querySelector("[data-process-output]").textContent = content.output;
  trustRoot.querySelector("[data-process-signal-label]").textContent =
    content.signalLabel;
  trustRoot.querySelector("[data-process-signal]").textContent = content.signal;
  processImage.src = content.image;
  processImage.alt = content.alt;
};

const setProcessVisualMode = () => {
  const isCatalog = activeProcessPath === "catalog";
  processSection.classList.toggle("is-catalog", isCatalog);
  processSection.dataset.activePath = activeProcessPath;
};

const preloadProcessPath = (path) => {
  processData[path].forEach(({ image }) => {
    void prepareSceneImage(image);
  });
};

const renderProcessSteps = () => {
  const steps = processData[activeProcessPath];

  processStepButtons.forEach((button, index) => {
    const content = steps[index];
    button.hidden = !content;
    if (!content) return;

    button.querySelector(".process-step__number").textContent = String(
      index + 1,
    ).padStart(2, "0");
    button.querySelector(".process-step__content strong").textContent =
      content.short;
    button.querySelector(".process-step__content small").textContent =
      content.summary;
  });
};

const setProcessStepState = (index) => {
  const steps = processData[activeProcessPath];
  const progress = steps.length ? (index + 0.5) / steps.length : 0;

  processStepButtons.forEach((button, buttonIndex) => {
    const isActive = buttonIndex === index && !button.hidden;
    button.classList.toggle("is-active", isActive);
    if (isActive) {
      button.setAttribute("aria-current", "step");
    } else {
      button.removeAttribute("aria-current");
    }
  });

  if (motionEnabled) {
    window.gsap.to(processProgress, {
      scaleY: progress,
      duration: 0.48,
      ease: "power3.out",
      overwrite: true,
    });
  } else {
    processProgress.style.transform = `scaleY(${progress})`;
  }
};

const activateProcessStep = async (index, animate = true) => {
  const steps = processData[activeProcessPath];
  if (!steps[index] || index === activeProcessStep) return false;

  activeProcessStep = index;
  setProcessStepState(index);
  const request = ++processSceneRequest;
  await prepareSceneImage(steps[index].image);
  if (request !== processSceneRequest) return false;

  if (!motionEnabled || !animate) {
    updateProcessContent(steps[index], index);
    return true;
  }

  const processNumber = trustRoot.querySelector("[data-process-number]");
  processStageTimeline?.kill();
  window.gsap.killTweensOf([
    ...processStageItems,
    processImage,
    processNumber,
  ]);

  processStageTimeline = window.gsap
    .timeline()
    .to(processStageItems, {
      y: -16,
      opacity: 0,
      duration: 0.18,
      stagger: 0.018,
      ease: "power2.in",
    })
    .to(
      processImage,
      {
        clipPath: "inset(0 0 100% 0)",
        scale: 1.08,
        duration: 0.32,
        ease: "power3.in",
      },
      0,
    )
    .to(
      processNumber,
      { y: 34, opacity: 0, duration: 0.2, ease: "power2.in" },
      0,
    )
    .add(() => updateProcessContent(steps[index], index))
    .set(processImage, { clipPath: "inset(100% 0 0 0)", scale: 1.12 })
    .set(processNumber, { y: -34 })
    .to(processImage, {
      clipPath: "inset(0% 0 0 0)",
      scale: 1,
      duration: 0.7,
      ease: "power4.out",
    })
    .to(
      processNumber,
      { y: 0, opacity: 1, duration: 0.46, ease: "power4.out" },
      "<+0.08",
    )
    .fromTo(
      processStageItems,
      { y: 22, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.46,
        stagger: 0.045,
        ease: "power4.out",
      },
      "<+0.02",
    );
  return true;
};

const activateMobileProcessStep = async (index) => {
  if (index === activeProcessStep) return;

  processStageTimeline?.kill();
  const activated = await activateProcessStep(index, false);

  if (!activated || !motionEnabled) return;

  const processNumber = trustRoot.querySelector("[data-process-number]");
  window.gsap.killTweensOf([
    ...processStageItems,
    processImage,
    processNumber,
  ]);
  window.gsap.fromTo(
    [...processStageItems, processNumber],
    { y: 10, opacity: 0.48 },
    {
      y: 0,
      opacity: 1,
      duration: 0.38,
      stagger: 0.025,
      ease: "power4.out",
      overwrite: true,
      clearProps: "transform,opacity",
    },
  );
  window.gsap.fromTo(
    processImage,
    { scale: 1.025, opacity: 0.62 },
    {
      scale: 1,
      opacity: 1,
      duration: 0.52,
      ease: "power4.out",
      overwrite: true,
      clearProps: "transform,opacity",
    },
  );
};

const getVisibleProcessSteps = () =>
  processStepButtons.filter((button) => !button.hidden);

const isProcessGateEngaged = () => {
  if (
    anchorNavigationActive ||
    staticControlAndProcess ||
    !motionEnabled ||
    window.matchMedia("(max-width: 760px)").matches
  ) {
    return false;
  }

  const processBody = trustRoot.querySelector(".process__body");
  const bodyRect = processBody.getBoundingClientRect();
  return bodyRect.top <= window.innerHeight * 0.18 &&
    bodyRect.bottom >= window.innerHeight * 0.82;
};

const scrollToProcessStep = (index) => {
  const button = getVisibleProcessSteps()[index];
  if (!button) return;

  const buttonRect = button.getBoundingClientRect();
  const targetTop =
    window.scrollY +
    buttonRect.top -
    Math.max(24, (window.innerHeight - buttonRect.height) / 2);

  processSnapGuard = true;
  window.clearTimeout(processSnapGuardTimer);
  window.scrollTo({
    top: targetTop,
    behavior: motionEnabled ? "smooth" : "auto",
  });
  processSnapGuardTimer = window.setTimeout(() => {
    processSnapGuard = false;
  }, processGateHoldMs + 220);
};

const unlockProcessGateAfterTransition = () => {
  window.clearTimeout(processGateUnlockTimer);
  processGateUnlockTimer = window.setTimeout(() => {
    processGateLocked = false;
  }, processGateHoldMs);
};

const requestAdjacentProcessStep = (requestedIndex, snap = true) => {
  const steps = processData[activeProcessPath];
  const requestedDirection = Math.sign(requestedIndex - activeProcessStep);
  if (!requestedDirection || processGateLocked) return;

  const nextIndex = Math.max(
    0,
    Math.min(steps.length - 1, activeProcessStep + requestedDirection),
  );
  if (nextIndex === activeProcessStep) return;

  processGateLocked = true;
  activateProcessStep(nextIndex);
  if (snap) {
    scrollToProcessStep(nextIndex);
  }
  if (motionEnabled) {
    unlockProcessGateAfterTransition();
  } else {
    processGateLocked = false;
  }
};

const setupProcessScrollTriggers = () => {
  processScrollTriggers.forEach((trigger) => trigger.kill());
  processScrollTriggers = [];

  if (!motionEnabled || staticControlAndProcess) return;

  const isMobileProcess = window.matchMedia("(max-width: 760px)").matches;

  getVisibleProcessSteps().forEach((button, index) => {
    processScrollTriggers.push(
      window.ScrollTrigger.create({
        trigger: button,
        start: isMobileProcess ? "top 84%" : "top 58%",
        end: isMobileProcess ? "bottom 84%" : "bottom 42%",
        onEnter: () => {
          if (anchorNavigationActive) return;

          if (isMobileProcess) {
            activateMobileProcessStep(index);
          } else if (!processSnapGuard && !processGateLocked) {
            requestAdjacentProcessStep(index);
          }
        },
        onEnterBack: () => {
          if (anchorNavigationActive) return;

          if (isMobileProcess) {
            activateMobileProcessStep(index);
          } else if (!processSnapGuard && !processGateLocked) {
            requestAdjacentProcessStep(index);
          }
        },
      }),
    );
  });
};

processStepButtons.forEach((button) => {
  button.addEventListener("click", () => {
    const index = Number(button.dataset.processStep);
    if (staticControlAndProcess) {
      activateProcessStep(index, false);
      return;
    }
    requestAdjacentProcessStep(index);
  });
});

window.addEventListener(
  "wheel",
  (event) => {
    const wheelEventAt = event.timeStamp || Date.now();
    const wheelDelta = Math.abs(event.deltaY);
    const startsNewImpulse =
      wheelEventAt - processWheelLastEventAt > processWheelResetMs;

    if (startsNewImpulse) {
      processWheelGestureConsumed = false;
      processWheelPeakDelta = wheelDelta;
      processWheelHasDecayed = false;
      processWheelIntentDistance = 0;
      processWheelIntentDirection = 0;
    } else {
      processWheelPeakDelta = Math.max(processWheelPeakDelta, wheelDelta);
      if (wheelDelta < processWheelPeakDelta * 0.42) {
        processWheelHasDecayed = true;
      }

      const isRenewedAcceleration =
        processWheelHasDecayed &&
        wheelDelta >= Math.max(20, processWheelPeakDelta * 0.58) &&
        wheelDelta > processWheelLastDelta * 1.45;
      const isSustainedIntent =
        processWheelGestureConsumed &&
        !processGateLocked &&
        wheelDelta >= Math.max(20, processWheelPeakDelta * 0.82) &&
        processWheelLastDelta >= processWheelPeakDelta * 0.72;
      if (isRenewedAcceleration || isSustainedIntent) {
        processWheelGestureConsumed = false;
        processWheelPeakDelta = wheelDelta;
        processWheelHasDecayed = false;
        processWheelIntentDistance = 0;
        processWheelIntentDirection = 0;
      }
    }

    processWheelLastEventAt = wheelEventAt;
    processWheelLastDelta = wheelDelta;

    if (!isProcessGateEngaged()) {
      processWheelGestureConsumed = false;
      processWheelLastEventAt = 0;
      processWheelPeakDelta = 0;
      processWheelLastDelta = 0;
      processWheelHasDecayed = false;
      processWheelIntentDistance = 0;
      processWheelIntentDirection = 0;
      return;
    }

    const direction = Math.sign(event.deltaY);
    if (!direction) return;

    if (processGateLocked) {
      event.preventDefault();
      return;
    }

    const lastStepIndex = processData[activeProcessPath].length - 1;
    const isLeavingSequence =
      (direction < 0 && activeProcessStep === 0) ||
      (direction > 0 && activeProcessStep === lastStepIndex);
    if (isLeavingSequence) return;

    event.preventDefault();
    if (processWheelGestureConsumed) return;

    if (processWheelIntentDirection !== direction) {
      processWheelIntentDirection = direction;
      processWheelIntentDistance = 0;
    }
    processWheelIntentDistance += wheelDelta;
    if (processWheelIntentDistance < getProcessWheelIntentThreshold()) return;

    processWheelIntentDistance = 0;
    processWheelGestureConsumed = true;
    requestAdjacentProcessStep(activeProcessStep + direction);
  },
  { passive: false },
);

window.addEventListener(
  "touchstart",
  (event) => {
    processTouchStartY = event.touches[0]?.clientY ?? null;
    processTouchGestureConsumed = false;
  },
  { passive: true },
);

window.addEventListener(
  "touchmove",
  (event) => {
    if (!isProcessGateEngaged() || processTouchStartY === null) return;

    const currentY = event.touches[0]?.clientY;
    if (currentY === undefined) return;

    const delta = processTouchStartY - currentY;
    const direction = Math.sign(delta);
    if (processGateLocked) {
      event.preventDefault();
      return;
    }

    const lastStepIndex = processData[activeProcessPath].length - 1;
    const isLeavingSequence =
      (direction < 0 && activeProcessStep === 0) ||
      (direction > 0 && activeProcessStep === lastStepIndex);
    if (isLeavingSequence) return;

    event.preventDefault();
    if (
      Math.abs(delta) < 44 ||
      processTouchGestureConsumed
    ) {
      return;
    }

    processTouchGestureConsumed = true;
    requestAdjacentProcessStep(activeProcessStep + direction);
  },
  { passive: false },
);

window.addEventListener(
  "touchend",
  () => {
    processTouchStartY = null;
    processTouchGestureConsumed = false;
  },
  { passive: true },
);

window.addEventListener("keydown", (event) => {
  if (
    event.repeat ||
    !isProcessGateEngaged() ||
    /INPUT|TEXTAREA|SELECT/.test(event.target.tagName)
  ) {
    return;
  }

  const forwardKeys = ["ArrowDown", "PageDown", " "];
  const backwardKeys = ["ArrowUp", "PageUp"];
  const direction = forwardKeys.includes(event.key)
    ? 1
    : backwardKeys.includes(event.key)
      ? -1
      : 0;
  if (!direction) return;

  if (processGateLocked) {
    event.preventDefault();
    return;
  }

  const lastStepIndex = processData[activeProcessPath].length - 1;
  const isLeavingSequence =
    (direction < 0 && activeProcessStep === 0) ||
    (direction > 0 && activeProcessStep === lastStepIndex);
  if (isLeavingSequence) return;

  event.preventDefault();
  requestAdjacentProcessStep(activeProcessStep + direction);
});

processPathButtons.forEach((button) => {
  button.addEventListener("pointerenter", () => {
    preloadProcessPath(button.dataset.processPath);
  });

  button.addEventListener("focus", () => {
    preloadProcessPath(button.dataset.processPath);
  });

  button.addEventListener("click", async () => {
    const nextPath = button.dataset.processPath;
    if (nextPath === activeProcessPath) return;

    activeProcessPath = nextPath;
    activeProcessStep = 0;
    const request = ++processSceneRequest;
    processGateLocked = false;
    processWheelGestureConsumed = false;
    processWheelLastEventAt = 0;
    processWheelPeakDelta = 0;
    processWheelLastDelta = 0;
    processWheelHasDecayed = false;
    processWheelIntentDistance = 0;
    processWheelIntentDirection = 0;
    processSnapGuard = false;
    window.clearTimeout(processGateUnlockTimer);
    window.clearTimeout(processSnapGuardTimer);
    setRovingTabs(processPathButtons, button);
    setProcessVisualMode();
    renderProcessSteps();
    setProcessStepState(0);
    await prepareSceneImage(processData[activeProcessPath][0].image);
    if (request !== processSceneRequest) return;

    processStageTimeline?.kill();
    if (motionEnabled) {
      window.gsap.killTweensOf([
        ...processStageItems,
        processImage,
        trustRoot.querySelector("[data-process-number]"),
      ]);
      window.gsap.set(processStageItems, { clearProps: "transform,opacity" });
      window.gsap.set(processImage, {
        clearProps: "transform,clipPath,opacity",
      });
      window.gsap.set("[data-process-number]", {
        clearProps: "transform,opacity",
      });
    }
    updateProcessContent(processData[activeProcessPath][0], 0);

    if (motionEnabled && !staticControlAndProcess) {
      window.gsap.fromTo(
        ".process__body",
        { opacity: 0.35, y: 18 },
        { opacity: 1, y: 0, duration: 0.58, ease: "power4.out" },
      );
      setupProcessScrollTriggers();
      window.ScrollTrigger.refresh();
    }
    if (!staticControlAndProcess) {
      scrollToProcessStep(0);
    }
  });

  button.addEventListener("keydown", (event) =>
    handleArrowNavigation(event, processPathButtons),
  );
});

setProcessVisualMode();
renderProcessSteps();
setProcessStepState(0);

const resetProcessGateForNavigation = () => {
  processGateLocked = false;
  processWheelGestureConsumed = false;
  processWheelLastEventAt = 0;
  processWheelPeakDelta = 0;
  processWheelLastDelta = 0;
  processWheelHasDecayed = false;
  processWheelIntentDistance = 0;
  processWheelIntentDirection = 0;
  processSnapGuard = false;
  processTouchStartY = null;
  processTouchGestureConsumed = false;
  window.clearTimeout(processGateUnlockTimer);
  window.clearTimeout(processSnapGuardTimer);
};

const navigateToPageAnchor = (hash) => {
  const target = trustRoot.querySelector(hash);
  if (!target) return;

  anchorNavigationActive = true;
  anchorNavigationToken += 1;
  const navigationToken = anchorNavigationToken;
  window.cancelAnimationFrame(anchorNavigationFrame);
  resetProcessGateForNavigation();

  const maxScroll = Math.max(
    0,
    document.documentElement.scrollHeight - window.innerHeight,
  );
  const targetTop = Math.min(
    maxScroll,
    Math.max(0, window.scrollY + target.getBoundingClientRect().top),
  );

  window.history.pushState(null, "", hash);
  window.scrollTo({
    top: targetTop,
    behavior: motionEnabled ? "smooth" : "auto",
  });

  if (!motionEnabled) {
    anchorNavigationActive = false;
    return;
  }

  const startedAt = window.performance.now();
  let settledFrames = 0;
  const monitorNavigation = () => {
    if (navigationToken !== anchorNavigationToken) return;

    if (Math.abs(window.scrollY - targetTop) < 2) {
      settledFrames += 1;
    } else {
      settledFrames = 0;
    }

    if (
      settledFrames >= 3 ||
      window.performance.now() - startedAt > 1800
    ) {
      anchorNavigationActive = false;
      window.ScrollTrigger?.update();
      return;
    }

    anchorNavigationFrame = window.requestAnimationFrame(monitorNavigation);
  };

  anchorNavigationFrame = window.requestAnimationFrame(monitorNavigation);
};

document
  .querySelectorAll('.site-header a[href^="#"]')
  .forEach((anchor) => {
    anchor.addEventListener("click", (event) => {
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const hash = anchor.getAttribute("href");
      if (!hash || hash === "#") return;

      event.preventDefault();
      navigateToPageAnchor(hash);
    });
  });

const controlNodes = [...trustRoot.querySelectorAll("[data-node]")];
const controlCount = trustRoot.querySelector("[data-control-count]");
const controlStatus = trustRoot.querySelector("[data-control-status]");
const controlMap = trustRoot.querySelector("[data-control-map]");
const controlCenter = trustRoot.querySelector(".control-map__center");
const controlCore = trustRoot.querySelector(".control-map__core");
const controlVortex = trustRoot.querySelector(".control-map__vortex");
const controlRoutes = [...trustRoot.querySelectorAll("[data-control-route]")];
const controlSignals = [...trustRoot.querySelectorAll(".control-node__signal")];

const setControlToFinalState = () => {
  controlNodes.forEach((node) => {
    node.style.opacity = "0";
  });
  controlCenter.style.opacity = "1";
  controlCenter.style.transform = "translate(-50%, -50%)";
  controlCount.textContent = "1";
  controlStatus.textContent = "partner accountable for every handoff";
};

const buildControlSignalFrames = (signal) => {
  const mapRect = controlMap.getBoundingClientRect();
  const signalRect = signal.getBoundingClientRect();
  const startX =
    signalRect.left + signalRect.width / 2 - (mapRect.left + mapRect.width / 2);
  const startY =
    signalRect.top + signalRect.height / 2 - (mapRect.top + mapRect.height / 2);
  const framePoints = [
    { progress: 0.18, scale: 1, opacity: 1 },
    { progress: 0.4, scale: 0.94, opacity: 1 },
    { progress: 0.64, scale: 0.82, opacity: 1 },
    { progress: 0.84, scale: 0.64, opacity: 1 },
    { progress: 1, scale: 0.16, opacity: 0 },
  ];

  return framePoints.map((frame, frameIndex) => {
    return {
      x: -startX * frame.progress,
      y: -startY * frame.progress,
      scale: frame.scale,
      opacity: frame.opacity,
      duration: frameIndex === framePoints.length - 1 ? 0.05 : 0.045,
      ease: frameIndex === framePoints.length - 1 ? "power2.in" : "none",
    };
  });
};

const setControlStartState = () => {
  const coordinateScale = Math.min(1, controlMap.offsetWidth / 640);
  const spacingScale = controlMap.offsetWidth >= 640 ? 1.18 : 1;

  controlNodes.forEach((node, index) => {
    node.style.setProperty("--signal-delay", `${index * 0.62}s`);
  });

  window.gsap.set(controlNodes, {
    xPercent: -50,
    yPercent: -50,
    x: (index, node) =>
      Number(node.dataset.x) * coordinateScale * spacingScale,
    y: (index, node) =>
      Number(node.dataset.y) * coordinateScale * spacingScale,
    scale: 1,
    opacity: 1,
  });
  window.gsap.set(controlSignals, {
    x: 0,
    y: 0,
    scale: 1,
    opacity: 1,
  });
  window.gsap.set(
    controlNodes.flatMap((node) => [
      node.querySelector(".control-node__index"),
      node.querySelector("strong"),
    ]),
    {
      y: 0,
      opacity: 1,
    },
  );
  window.gsap.set(controlCenter, {
    xPercent: -50,
    yPercent: -50,
    scale: 0.96,
    rotate: 0,
    opacity: 0,
    clipPath: "inset(0 50% 0 50%)",
  });
  window.gsap.set(controlCenter.querySelectorAll(".brand__site-logo, strong, small"), {
    y: 12,
    opacity: 0,
  });
  window.gsap.set(controlCore, {
    xPercent: -50,
    yPercent: -50,
    scale: 0.82,
    opacity: 0.86,
  });
  window.gsap.set(controlVortex, {
    transformOrigin: "50% 50%",
    rotate: 0,
    scale: 1,
    opacity: 1,
  });
  controlRoutes.forEach((route, index) => {
    const node = controlNodes[index];
    const routeX = 320 + Number(node.dataset.x) * spacingScale;
    const routeY = 320 + Number(node.dataset.y) * spacingScale;
    route.setAttribute("d", `M${routeX} ${routeY}L320 320`);
    const routeLength = route.getTotalLength();
    window.gsap.set(route, {
      strokeDasharray: routeLength,
      strokeDashoffset: routeLength,
      opacity: 0.14,
    });
  });

  return coordinateScale;
};

const addControlVortexSequence = (timeline, coordinateScale) => {
  controlNodes.forEach((node, index) => {
    const entryStart = 0.04 + index * 0.24;
    const route = controlRoutes[index];
    const signal = controlSignals[index];
    const nodeLabels = node.querySelectorAll(".control-node__index, strong");

    timeline.to(
      route,
      {
        strokeDashoffset: 0,
        opacity: 0.76,
        duration: 0.07,
        ease: "none",
      },
      entryStart,
    );
    timeline.to(
      nodeLabels,
      {
        y: -8,
        opacity: 0,
        duration: 0.08,
        stagger: 0.012,
        ease: "power4.out",
      },
      entryStart + 0.015,
    );
    timeline.to(
      signal,
      {
        keyframes: buildControlSignalFrames(signal),
      },
      entryStart + 0.035,
    );
    timeline.to(
      controlCore,
      {
        scale: 1.32,
        opacity: 1,
        duration: 0.045,
        ease: "power3.out",
      },
      entryStart + 0.19,
    );
    timeline.to(
      controlCore,
      {
        scale: 0.78,
        opacity: 0.72,
        duration: 0.07,
        ease: "power4.out",
      },
      entryStart + 0.235,
    );
    timeline.to(
      route,
      {
        opacity: 0,
        duration: 0.1,
        ease: "power2.out",
      },
      entryStart + 0.2,
    );
    timeline.to(
      node,
      {
        opacity: 0,
        duration: 0.08,
        ease: "power3.out",
      },
      entryStart + 0.24,
    );
  });

  timeline
    .to(
      controlCore,
      {
        scale: 0.54,
        opacity: 0,
        duration: 0.18,
        ease: "power4.out",
      },
      1.26,
    )
    .to(
      controlCenter,
      {
        scale: 1,
        rotate: 0,
        opacity: 1,
        clipPath: "inset(0 0% 0 0%)",
        duration: 0.34,
        ease: "power4.out",
      },
      1.28,
    )
    .to(
      controlCenter.querySelectorAll(".brand__site-logo, strong, small"),
      {
        y: 0,
        opacity: 1,
        duration: 0.22,
        stagger: 0.035,
        ease: "power4.out",
      },
      1.36,
    );
};

const initHeroMotion = () => {
  const heroTimeline = window.gsap.timeline({
    paused: true,
    defaults: { ease: "power4.out" },
    onComplete: () => {
      trustRoot.classList.remove("motion-boot");
    },
  });

  window.gsap.set(".hero-title__line > span", { yPercent: 128 });
  window.gsap.set(
    [
      ".hero__eyebrow",
      ".hero__intro",
      ".hero__actions",
      ".proof-console",
      ".hero__hint",
    ],
    { opacity: 0 },
  );

  heroTimeline
    .fromTo(
      ".hero__media img",
      { scale: 1.08 },
      { scale: 1, duration: 0.56 },
      0,
    )
    .to(".hero__eyebrow", { opacity: 1, duration: 0.18 }, "<+0.08")
    .to(
      ".hero-title__line > span",
      { yPercent: 0, duration: 0.42, stagger: 0.06 },
      "<+0.02",
    )
    .fromTo(
      ".hero__intro",
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.28 },
      "<+0.16",
    )
    .fromTo(
      ".hero__actions",
      { y: 16, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.24 },
      "<+0.04",
    )
    .fromTo(
      ".proof-console",
      { y: 42, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.3 },
      "<+0.02",
    )
    .to(".hero__hint", { opacity: 1, duration: 0.18 }, "<+0.1");

  if (window.matchMedia("(min-width: 761px)").matches) {
    window.gsap.to(".hero__media", {
      yPercent: 8,
      scale: 1.06,
      ease: "none",
      scrollTrigger: {
        trigger: ".hero",
        start: "top top",
        end: "bottom top",
        scrub: 1.1,
      },
    });
  }

  return heroTimeline;
};

const initScrollReveals = () => {
  window.gsap.from(".problem__lead > *", {
    y: 58,
    opacity: 0,
    duration: 0.72,
    stagger: 0.09,
    ease: "power4.out",
    scrollTrigger: {
      trigger: ".problem",
      start: "top 74%",
    },
  });

  window.gsap.from(".problem__list p", {
    x: 58,
    opacity: 0,
    duration: 0.62,
    stagger: 0.1,
    ease: "power4.out",
    scrollTrigger: {
      trigger: ".problem__list",
      start: "top 76%",
    },
  });

  if (!staticControlAndProcess) {
    window.gsap.from(".control__copy > *", {
      y: 44,
      opacity: 0,
      duration: 0.68,
      stagger: 0.075,
      ease: "power4.out",
      scrollTrigger: {
        trigger: ".control",
        start: "top 72%",
      },
    });
  }

  window.gsap.from(".warehouse__header > *", {
    y: 52,
    opacity: 0,
    duration: 0.68,
    stagger: 0.08,
    ease: "power4.out",
    scrollTrigger: {
      trigger: ".warehouse__header",
      start: "top 78%",
    },
  });

  const warehouseTimeline = window.gsap.timeline({
    scrollTrigger: {
      trigger: ".warehouse__experience",
      start: "top 76%",
    },
  });

  warehouseTimeline
    .from(warehouseImage, {
      clipPath: "inset(12% 16% 12% 16%)",
      duration: 0.88,
      ease: "power4.out",
    })
    .from(
      ".warehouse__detail > *",
      {
        y: 28,
        opacity: 0,
        duration: 0.48,
        stagger: 0.055,
        ease: "power4.out",
      },
      "<+0.18",
    )
    .from(
      hotspotButtons,
      {
        scale: 0,
        opacity: 0,
        duration: 0.42,
        stagger: 0.08,
        ease: "power4.out",
      },
      "<+0.04",
    );

  if (!staticControlAndProcess) {
    window.gsap.from(".process__header > *", {
      y: 52,
      opacity: 0,
      duration: 0.72,
      stagger: 0.1,
      ease: "power4.out",
      scrollTrigger: {
        trigger: ".process__header",
        start: "top 78%",
      },
    });

    const processTimeline = window.gsap.timeline({
      scrollTrigger: {
        trigger: ".process__body",
        start: "top 76%",
      },
    });

    processTimeline
      .from(processImage, {
        clipPath: "inset(10% 12% 10% 12%)",
        scale: 1.12,
        duration: 0.9,
        ease: "power4.out",
      })
      .from(
        processStageItems,
        {
          y: 28,
          opacity: 0,
          duration: 0.48,
          stagger: 0.05,
          ease: "power4.out",
        },
        "<+0.16",
      )
      .from(
        processStepButtons,
        {
          x: 42,
          opacity: 0,
          duration: 0.5,
          stagger: 0.08,
          ease: "power4.out",
        },
        "<+0.04",
      );
  }

  window.gsap.from(".clients__header > *", {
    y: 52,
    opacity: 0,
    duration: 0.68,
    stagger: 0.08,
    ease: "power4.out",
    scrollTrigger: {
      trigger: ".clients__header",
      start: "top 78%",
    },
  });

  const clientsTimeline = window.gsap.timeline({
    scrollTrigger: {
      trigger: ".clients__experience",
      start: "top 76%",
    },
  });

  clientsTimeline
    .from(".client-selector__button", {
      x: -36,
      opacity: 0,
      duration: 0.48,
      stagger: 0.08,
      ease: "power4.out",
    })
    .from(
      clientImage,
      {
        clipPath: "inset(0 0 100% 0)",
        scale: 1.1,
        duration: 0.82,
        ease: "power4.out",
      },
      "<+0.06",
    )
    .from(
      clientCopyItems,
      {
        y: 28,
        opacity: 0,
        duration: 0.46,
        stagger: 0.055,
        ease: "power4.out",
      },
      "<+0.16",
    );

  window.gsap.from(".faq__intro > *", {
    y: 48,
    opacity: 0,
    duration: 0.68,
    stagger: 0.08,
    ease: "power4.out",
    scrollTrigger: {
      trigger: ".faq",
      start: "top 76%",
    },
  });

  window.gsap.from(".faq-item", {
    y: 28,
    opacity: 0,
    duration: 0.52,
    stagger: 0.065,
    ease: "power4.out",
    scrollTrigger: {
      trigger: ".faq__list",
      start: "top 78%",
    },
  });

  window.gsap.from(".contact__content > *", {
    y: 48,
    opacity: 0,
    duration: 0.72,
    stagger: 0.09,
    ease: "power4.out",
    scrollTrigger: {
      trigger: ".contact",
      start: "top 70%",
    },
  });

  window.gsap.fromTo(
    ".contact__image img",
    { yPercent: -7, scale: 1.09 },
    {
      yPercent: 7,
      scale: 1.02,
      ease: "none",
      scrollTrigger: {
        trigger: ".contact",
        start: "top bottom",
        end: "bottom top",
        scrub: 1.2,
      },
    },
  );
};

const initControlMotion = () => {
  const coordinateScale = setControlStartState();

  const controlTimeline = window.gsap.timeline({
    scrollTrigger: {
      trigger: ".control",
      start: "top top",
      end: "+=350%",
      pin: ".control__sticky",
      scrub: 1.35,
      anticipatePin: 1,
      onUpdate: (self) => {
        const thresholds = [0.15, 0.27, 0.39, 0.51];
        const absorbed = thresholds.filter(
          (threshold) => self.progress >= threshold,
        ).length;
        const remaining = Math.max(1, 5 - absorbed);
        controlCount.textContent = String(remaining);
        if (self.progress < 0.08) {
          controlStatus.textContent = "separate handoffs moving independently";
        } else if (self.progress < 0.68) {
          controlStatus.textContent = "handoffs entering one controlled system";
        } else {
          controlStatus.textContent = "partner accountable for every handoff";
        }
      },
    },
  });

  addControlVortexSequence(controlTimeline, coordinateScale);
  controlTimeline.to(controlCenter, {
    scale: 1,
    duration: 0.48,
    ease: "none",
  });
};

const initMobileControlMotion = () => {
  const coordinateScale = setControlStartState();
  const mobileTimeline = window.gsap.timeline({
    scrollTrigger: {
      trigger: ".control-map",
      start: "top 78%",
      once: true,
    },
    onUpdate: function updateMobileStatus() {
      const progress = this.progress();
      const thresholds = [0.2, 0.35, 0.5, 0.65];
      const absorbed = thresholds.filter(
        (threshold) => progress >= threshold,
      ).length;
      controlCount.textContent = String(Math.max(1, 5 - absorbed));
      controlStatus.textContent =
        progress > 0.83
          ? "partner accountable for every handoff"
          : "handoffs entering one controlled system";
    },
  });

  addControlVortexSequence(mobileTimeline, coordinateScale);
  mobileTimeline.duration(3.4);
};

const initMagneticButtons = () => {
  if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
    return;
  }

  trustRoot.querySelectorAll(".button, .header-cta").forEach((element) => {
    const moveX = window.gsap.quickTo(element, "x", {
      duration: 0.34,
      ease: "power4.out",
    });
    const moveY = window.gsap.quickTo(element, "y", {
      duration: 0.34,
      ease: "power4.out",
    });

    element.addEventListener("pointermove", (event) => {
      const bounds = element.getBoundingClientRect();
      moveX((event.clientX - bounds.left - bounds.width / 2) * 0.15);
      moveY((event.clientY - bounds.top - bounds.height / 2) * 0.2);
    });

    element.addEventListener("pointerleave", () => {
      moveX(0);
      moveY(0);
    });
  });
};

const initDesktopParallax = () => {
  const hero = trustRoot.querySelector(".hero");
  const contentX = window.gsap.quickTo(".hero__content", "x", {
    duration: 0.8,
    ease: "power4.out",
  });
  const contentY = window.gsap.quickTo(".hero__content", "y", {
    duration: 0.8,
    ease: "power4.out",
  });
  const consoleX = window.gsap.quickTo(".proof-console", "x", {
    duration: 1,
    ease: "power4.out",
  });

  hero.addEventListener("pointermove", (event) => {
    const xRatio = event.clientX / window.innerWidth - 0.5;
    const yRatio = event.clientY / window.innerHeight - 0.5;
    contentX(xRatio * -8);
    contentY(yRatio * -5);
    consoleX(xRatio * 6);
  });

  hero.addEventListener("pointerleave", () => {
    contentX(0);
    contentY(0);
    consoleX(0);
  });
};

const initMotion = (heroTimeline = null, skipHeroMotion = false) => {
  if (!motionEnabled) {
    trustRoot.classList.remove("motion-boot");
    document.documentElement.classList.remove("trust-page-loading");
    document.querySelector("[data-trust-page-loader]")?.remove();
    if (!staticControlAndProcess) {
      setControlToFinalState();
    }
    trustRoot.querySelectorAll(".reveal").forEach((element) => {
      element.classList.add("is-visible");
    });
    return;
  }

  const activeHeroTimeline = skipHeroMotion
    ? null
    : heroTimeline || initHeroMotion();
  if (skipHeroMotion) {
    trustRoot.classList.remove("motion-boot");
    document.documentElement.classList.remove("trust-page-loading");
    pageLoader?.remove();
  }
  const initDeferredMotion = () => {
    initScrollReveals();

    const media = window.gsap.matchMedia();
    media.add("(min-width: 761px)", () => {
      if (!staticControlAndProcess) {
        initControlMotion();
        setupProcessScrollTriggers();
      }
      initMagneticButtons();
      initDesktopParallax();
    });
    media.add("(max-width: 760px)", () => {
      if (!staticControlAndProcess) {
        initMobileControlMotion();
        setupProcessScrollTriggers();
      }
      initMobileClientScroll();
    });

    window.ScrollTrigger.refresh();
  };

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(initDeferredMotion, { timeout: 1200 });
  } else {
    window.setTimeout(initDeferredMotion, 320);
  }
  return activeHeroTimeline;
};

if (!gsapAvailable) {
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          revealObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.13 },
  );

  trustRoot.querySelectorAll(".reveal").forEach((element) => {
    revealObserver.observe(element);
  });
}

const initMotionAndRestoreScroll = (
  heroTimeline = null,
  skipHeroMotion = false,
) => {
  const activeHeroTimeline = initMotion(heroTimeline, skipHeroMotion);

  if (!shouldRestoreScroll || savedScrollY <= 0) {
    return activeHeroTimeline;
  }

  window.requestAnimationFrame(() => {
    if (gsapAvailable) {
      window.ScrollTrigger.refresh();
    }
    window.scrollTo({ top: savedScrollY, behavior: "auto" });
    window.requestAnimationFrame(() => {
      if (gsapAvailable) {
        window.ScrollTrigger.update();
      }
    });
  });

  return activeHeroTimeline;
};

const pageLoader = document.querySelector("[data-trust-page-loader]");

const waitForCriticalImage = async (image) => {
  if (!image) return;

  if (!image.complete) {
    await new Promise((resolve) => {
      image.addEventListener("load", resolve, { once: true });
      image.addEventListener("error", resolve, { once: true });
    });
  }

  try {
    await image.decode?.();
  } catch {
    // The load event is enough to release the page if decoding is unavailable.
  }
};

const waitForCriticalStyles = async () => {
  const stylesheet = document.querySelector("[data-trust-homepage-style]");
  if (!stylesheet || stylesheet.dataset.ready === "true" || stylesheet.sheet) {
    return;
  }

  await new Promise((resolve) => {
    stylesheet.addEventListener("load", resolve, { once: true });
    stylesheet.addEventListener("error", resolve, { once: true });
  });
};

const waitForEssentialResources = async () => {
  const resourcesReady = Promise.all([
    waitForCriticalStyles(),
    waitForCriticalImage(heroBaseImage),
  ]);
  const timeout = new Promise((resolve) => window.setTimeout(resolve, 4200));

  await Promise.race([resourcesReady, timeout]);
  await new Promise((resolve) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
  });
};

const revealHomepage = async () => {
  await waitForEssentialResources();
  const loaderStillActive =
    pageLoader?.isConnected && !window.__trustLoaderExpired;
  const heroTimeline =
    motionEnabled && loaderStillActive ? initHeroMotion() : null;

  const activeHeroTimeline = initMotionAndRestoreScroll(
    heroTimeline,
    !loaderStillActive,
  );
  window.__releaseTrustLoader?.();
  if (activeHeroTimeline) {
    activeHeroTimeline.play(0);
  }
};

revealHomepage();

window.setTimeout(() => {
  window.__releaseTrustLoader?.();
}, 4600);
})();
